// ============================================================================
// TENANT USER SERVICE
// ============================================================================
// Business logic and API calls for Tenant User Management module

import { get, post, put, del } from '../client/axiosClient';
import { tenantUserEndpoints } from '../endpoints';
import type {
  ID,
  TenantUserResponse,
  CreateTenantUserRequest,
  UpdateTenantUserRequest,
  SetPasswordRequest,
} from '../types';
import type { TenantHubApiResponse } from '../types/auth.types';
import type { TenantHubPagedResult, TenantHubPagedRequest } from '../types/tenants.types';

/**
 * Tenant User Service Class
 */
class TenantUserService {
  /**
   * Get all users for a tenant (paginated)
   */
  async getAll(tenantId: ID, params?: TenantHubPagedRequest): Promise<TenantHubApiResponse<TenantHubPagedResult<TenantUserResponse>>> {
    const response = await get<TenantHubApiResponse<TenantHubPagedResult<TenantUserResponse>>>(
      tenantUserEndpoints.list(tenantId),
      { params }
    );
    return response.data;
  }

  /**
   * Get user by ID
   */
  async getById(tenantId: ID, userId: ID): Promise<TenantHubApiResponse<TenantUserResponse>> {
    const response = await get<TenantHubApiResponse<TenantUserResponse>>(tenantUserEndpoints.getById(tenantId, userId));
    return response.data;
  }

  /**
   * Create new user in tenant
   */
  async create(tenantId: ID, data: CreateTenantUserRequest): Promise<TenantHubApiResponse<TenantUserResponse>> {
    const response = await post<TenantHubApiResponse<TenantUserResponse>>(
      tenantUserEndpoints.create(tenantId),
      data
    );
    return response.data;
  }

  /**
   * Update user
   */
  async update(tenantId: ID, userId: ID, data: UpdateTenantUserRequest): Promise<TenantHubApiResponse<TenantUserResponse>> {
    const response = await put<TenantHubApiResponse<TenantUserResponse>>(
      tenantUserEndpoints.update(tenantId, userId),
      data
    );
    return response.data;
  }

  /**
   * Delete user
   */
  async delete(tenantId: ID, userId: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await del<TenantHubApiResponse<boolean>>(tenantUserEndpoints.delete(tenantId, userId));
    return response.data;
  }

  /**
   * Reset user password (admin)
   */
  async resetPassword(tenantId: ID, userId: ID, data: SetPasswordRequest): Promise<TenantHubApiResponse<boolean>> {
    const response = await put<TenantHubApiResponse<boolean>>(
      tenantUserEndpoints.resetPassword(tenantId, userId),
      data
    );
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const tenantUserService = new TenantUserService();
export default tenantUserService;
